<template>
  <div class="route">
    <div class="form-row options">
      <select v-model="way" class="sml" name="way" aria-label="route type" @change="init">
        <option value="roundtrip">Round trip</option>
        <option value="oneway">One way</option>
        <option value="multicity">Multi city</option>
      </select>
      <select v-model="cabin" class="sml" name="cabin" aria-label="cabin class">
        <option value="economy">Economy</option>
        <option value="premium-economy">Premium Eco.</option>
        <option value="business-first">Business/First</option>
      </select>
      <select v-model="pax" class="sml" name="pax" aria-label="number of passengers">
        <option v-for="n in 250" :key="n" :value="n">{{ n }} {{ n == 1 ? "Person" : "People" }}</option>
      </select>
    </div>
    <div class="form-section legs" :class="way">
      <div v-for="(leg, index) in routeLegs" :key="leg.key" class="form-row leg">
        <AirportPicker v-model:airport="leg.from" class="airport from" placeholder="From" @error="$emit('error', $event)" />
        <div class="break" />
        <AirportPicker v-model:airport="leg.to" class="airport to" placeholder="To" @error="$emit('error', $event)" />
        <div class="break" />
        <DatePicker v-model:date="leg.depart" class="depart" :min="way == 'multicity' && index > 0 ? route[index - 1].depart : ''" :max="way == 'roundtrip' && index == 0 && route[index + 1] ? route[index + 1].depart : ''" :placeholder="'Depart'" />
        <DatePicker v-if="way == 'roundtrip'" v-model:date="returnDate" class="return" :min="leg.depart ? leg.depart : ''" :placeholder="'Return'" />
        <a v-if="way == 'multicity'" class="remove" @click="legRemove(index)">&cross;</a>
      </div>
      <div v-if="way == 'multicity'" class="add">
        <a @click="legAdd">Add Flight</a>
      </div>
    </div>
    <div id="navigation">
      <button @click="next">Next Step</button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import AirportPicker from "./common/PickerAirport.vue";
import DatePicker from "./common/PickerDate.vue";

const props = defineProps(["flights", "isPremium"]);
const emit = defineEmits(["update:flights", "update:isSavedRoute", "update:legIndex", "scroll", "error"]);

// reactive state
const cabin = ref(props.flights.cabin);
const pax = ref(props.flights.pax);
const returnDate = ref(props.flights.route.length == 2 ? props.flights.route[1].depart : "");
const route = ref([...props.flights.route]);
const way = ref(props.flights.way);

// computed properties
const routeLegs = computed(() => {
  if (way.value == "roundtrip") {
    return route.value.slice(0, 1);
  } else {
    return route.value;
  }
});

// watchers
watch(
  route,
  () => {
    if (way.value == "roundtrip") {
      route.value[1].from = route.value[0].to;
      route.value[1].to = route.value[0].from;
    }
  },
  { deep: true }
);

watch(returnDate, () => {
  if (way.value == "roundtrip") {
    route.value[1].depart = returnDate.value;
  }
});

// methods
const blank = () => {
  const key = route.value.length + 1;
  return {
    key,
    depart: "",
    from: {},
    ideal: {},
    to: {},
  };
};

const init = () => {
  switch (way.value) {
    case "roundtrip":
      if (route.value.length > 2) {
        route.value = route.value.splice(0, 2);
        route.value[1].from = route.value[0].to;
        route.value[1].to = route.value[0].from;
      } else if (route.value.length == 2) {
        route.value[1].from = route.value[0].to;
        route.value[1].to = route.value[0].from;
        route.value[1].depart = returnDate.value;
      } else if (route.value.length == 1) {
        route.value.push({
          depart: returnDate.value,
          from: route.value[0].to,
          ideal: [],
          to: route.value[0].from,
        });
      } else if (route.value.length == 0) {
        route.value = [blank(), blank()];
      }
      break;
    case "oneway":
      if (route.value.length > 1) {
        route.value = route.value.splice(0, 1);
      } else if (route.value.length == 0) {
        route.value = [blank()];
      }
      break;
    case "multicity":
      if (route.value.length == 2 && route.value[0].to == route.value[1].from) {
        const outbound = route.value[0];
        const inbound = route.value[1];
        route.value = [];
        route.value.push(outbound);
        route.value.push(inbound);
      } else if (route.value.length == 1) {
        route.value.push(blank());
      }
      break;
    default:
      route.value = [blank(), blank()];
  }
};

const legAdd = () => {
  route.value.push(blank());
};

const legRemove = index => {
  route.value.splice(index, 1);
};

const next = () => {
  let validated = true;
  for (let i = 0; i < route.value.length; i++) {
    if (!route.value[i].depart || !route.value[i].from || !route.value[i].to) {
      validated = false;
    }
  }

  if (validated === false) {
    alert("Please select airports and dates for all legs before continuing.");
  } else {
    route.value.forEach(leg => {
      leg.ideal = {};
    });

    const newFlights = {
      ...props.flights,
      cabin: cabin.value,
      pax: pax.value,
      route: route.value,
      way: way.value,
    };

    emit("update:flights", newFlights);
    emit("update:isSavedRoute", true);
    emit("update:legIndex", 0);
    emit("scroll");
  }
};
</script>

<style lang="less">
#flights {
  .route {
    clear: both;

    select,
    input {
      margin-bottom: 1rem;
    }

    .leg {
      flex-wrap: wrap;
    }

    .break {
      flex-basis: 100%;
      height: 0;
    }

    .picker,
    .date-picker {
      flex: 1;
    }

    .remove {
      background: none;
      border: none;
      padding: 1rem 0;
      text-align: right;
      width: 2rem;
    }
  }
}

@media screen and (min-width: 30em) {
  #flights {
    .route {
      .options {
        select {
          flex: 1;
          margin-right: 1rem;

          &:last-child {
            margin-right: 0;
          }
        }
      }
    }
  }
}

@media screen and (min-width: 60em) {
  #flights {
    .route {
      select,
      input {
        margin-bottom: 0;
      }

      .options {
        select {
          flex: none;
        }
      }

      .from {
        margin-right: 1rem;
      }

      .to {
        margin-right: 1rem;
      }

      .roundtrip .depart {
        margin-right: 1rem;
      }

      .break {
        display: none;
      }
    }
  }
}
</style>
